<div class="min-h-screen px-4 sm:px-6 lg:px-8 py-12">
  <div class="container mx-auto">
    <!-- 🌠 Encabezado -->
    <div class="text-center lg:text-left mb-10">
      <h1
        class="text-4xl lg:text-5xl font-bold mb-2 animate-fade-slide-up text-gray-100"
      >
        Carrito de Compras
      </h1>
      <p
        class="text-gray-400 animate-fade-slide-up"
        style="animation-delay: 0.2s"
      >
        Verifica los detalles de tu misión de compra.
      </p>
    </div>

    <!-- Esqueleto de Carga -->
    @if (isLoading) {
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div class="lg:col-span-2 space-y-6">
          <app-galaxy-card padding="lg">
            <div galaxy-card-content class="space-y-4">
              <app-galaxy-skeleton
                height="2rem"
                width="60%"
              ></app-galaxy-skeleton>
              @for (i of [1, 2, 3]; track i) {
                <div
                  class="flex items-center space-x-4 py-4 border-t border-white/10"
                >
                  <app-galaxy-skeleton
                    height="5rem"
                    width="5rem"
                    borderRadius="0.5rem"
                  ></app-galaxy-skeleton>
                  <div class="flex-grow space-y-2">
                    <app-galaxy-skeleton
                      height="1.25rem"
                      width="80%"
                    ></app-galaxy-skeleton>
                    <app-galaxy-skeleton
                      height="1rem"
                      width="40%"
                    ></app-galaxy-skeleton>
                  </div>
                  <app-galaxy-skeleton
                    height="1.5rem"
                    width="20%"
                  ></app-galaxy-skeleton>
                </div>
              }
            </div>
          </app-galaxy-card>
        </div>
        <div class="lg:col-span-1">
          <app-galaxy-card padding="lg">
            <div galaxy-card-content class="space-y-6">
              <app-galaxy-skeleton
                height="2rem"
                width="50%"
              ></app-galaxy-skeleton>
              <app-galaxy-skeleton
                height="8rem"
                width="100%"
              ></app-galaxy-skeleton>
              <app-galaxy-skeleton
                height="6rem"
                width="100%"
              ></app-galaxy-skeleton>
              <app-galaxy-skeleton
                height="3rem"
                width="100%"
                borderRadius="9999px"
              ></app-galaxy-skeleton>
            </div>
          </app-galaxy-card>
        </div>
      </div>
    }

    <!-- Carrito Vacío -->
    @if (!isLoading && enrichedCartItems.length === 0) {
      <div>
        <app-galaxy-card class="text-center animate-fade-slide-up" padding="lg">
          <div galaxy-card-content>
            <div class="max-w-md mx-auto">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-24 h-24 sm:w-28 sm:h-28 text-violet-400/40 mx-auto mb-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="1"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-14L4 7m0 0v10l8 4m0-14L4 7m8 4v10"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M4.75 8.25l7.5 4 7.5-4"
                />
              </svg>
              <h2 class="text-2xl sm:text-3xl font-bold text-gray-100">
                Tu nave de carga está vacía
              </h2>
              <p class="text-gray-400 mt-3 mb-8">
                No hay nada por aquí. Añade productos desde la tienda para
                iniciar tu próxima misión de compra.
              </p>
              <button
                [routerLink]="['/tienda']"
                appGalaxyButton
                appGalaxyRipple
                class="galaxy-variant-primary galaxy-size-md"
              >
                Explorar la Tienda
              </button>
            </div>
          </div>
        </app-galaxy-card>
      </div>
    }

    <!-- Contenido del Carrito -->
    @if (!isLoading && enrichedCartItems.length > 0) {
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Columna Izquierda: Items -->
        <div
          class="lg:col-span-2 space-y-6 animate-fade-slide-up"
          style="animation-delay: 0.4s"
        >
          <app-galaxy-card padding="lg">
            <div galaxy-card-content>
              <h2
                class="text-2xl font-semibold text-violet-300 mb-4 border-b border-white/10 pb-3"
              >
                Contenido del Paquete ({{ enrichedCartItems.length }})
              </h2>
              <ul class="divide-y divide-white/10" appAnimateStagger>
                @for (item of enrichedCartItems; track item) {
                  <li
                    #staggerItem
                    class="flex flex-col sm:flex-row items-start sm:items-center py-5 space-y-4 sm:space-y-0"
                    [ngClass]="{ 'opacity-50': !item.active }"
                  >
                    <img
                      [src]="
                        item.imageUrl ||
                        'https://placehold.co/100x100/0a040d/a78bfa?text=Eagle'
                      "
                      [alt]="item.name"
                      class="w-24 h-24 sm:w-20 sm:h-20 object-cover rounded-lg mr-5 border-2 border-white/10 flex-shrink-0"
                    />
                    <div class="flex-grow">
                      <p class="font-bold text-lg text-gray-100">
                        {{ item.name }}
                      </p>
                      <p
                        class="text-sm font-medium"
                        [ngClass]="{
                          'text-red-400': !item.active,
                          'text-gray-400': item.active,
                        }"
                      >
                        {{ item.active ? "En stock" : "No disponible" }}
                      </p>
                    </div>
                    <div class="flex items-center justify-center space-x-3">
                      <button
                        (click)="decreaseQuantity(item)"
                        [disabled]="!item.active"
                        appGalaxyButton
                        class="galaxy-variant-secondary !rounded-full !w-8 !h-8 !p-0 flex items-center justify-center"
                      >
                        -
                      </button>
                      <span
                        class="font-semibold text-lg w-8 text-center text-gray-200"
                        >{{ item.quantity }}</span
                      >
                      <button
                        (click)="increaseQuantity(item)"
                        [disabled]="!item.active"
                        appGalaxyButton
                        class="galaxy-variant-secondary !rounded-full !w-8 !h-8 !p-0 flex items-center justify-center"
                      >
                        +
                      </button>
                    </div>
                    <div class="text-right pl-4 flex-shrink-0">
                      <p class="font-bold text-xl text-violet-300">
                        {{ item.price * item.quantity | currency }}
                      </p>
                      <button
                        (click)="deleteItemCart(item.id)"
                        appGalaxyButton
                        class="galaxy-variant-ghost galaxy-size-sm !px-2 !py-1 mt-1"
                      >
                        Eliminar
                      </button>
                    </div>
                  </li>
                }
              </ul>
            </div>
          </app-galaxy-card>
          <!-- Card: Información de Envío -->
          <app-galaxy-card padding="lg">
            <div galaxy-card-content>
              <h2 class="text-2xl font-semibold text-violet-300 mb-4 border-b border-white/10 pb-3">
                Coordenadas de Entrega
              </h2>
              
              <div class="space-y-4">
                <div>
                  <label class="block mb-2 text-sm font-medium text-gray-300">Dirección de Envío</label>
                  <input
                    type="text"
                    [(ngModel)]="shippingAddress"
                    placeholder="Ej. Cl. 84c Bis Sur #80i-53, Bogotá"
                    class="glass-input w-full"
                  />
                  @if (!shippingAddress || !shippingAddress.trim()) {
                    <p class="text-red-400 text-xs mt-1">La dirección es requerida para el despacho.</p>
                  }
                </div>
                
                <div>
                  <label class="block mb-2 text-sm font-medium text-gray-300">Teléfono Celular</label>
                  <input
                    type="tel"
                    [(ngModel)]="cellphone"
                    placeholder="Tu número para contactarte"
                    class="glass-input w-full"
                  />
                  @if (!cellphone || !cellphone.trim()) {
                    <p class="text-red-400 text-xs mt-1">El teléfono es requerido para contactarte.</p>
                  }
                </div>
              </div>
            </div>
          </app-galaxy-card>
        </div>
        <!-- Columna Derecha: Resumen y Pago -->
        <div
          class="lg:col-span-1 animate-fade-slide-up"
          style="animation-delay: 0.6s"
        >
          <app-galaxy-card class="sticky top-24" padding="lg">
            <div galaxy-card-content>
              <h2
                class="text-2xl font-semibold text-violet-300 mb-4 border-b border-white/10 pb-3"
              >
                Centro de Mando
              </h2>
              <!-- Método de Pago -->
              <div class="my-6">
                <h3 class="text-lg font-semibold text-gray-200 mb-4">
                  Canal de Pago
                </h3>
                <div class="space-y-4">
                  <label
                    class="flex items-center p-4 rounded-xl cursor-pointer transition-all duration-300 bg-smoked-slate shadow-neumorphic-dark-sm hover:shadow-neumorphic-dark-sm-hover"
                    [ngClass]="{
                      '!shadow-neumorphic-dark-inset-sm':
                        selectedPaymentMethod === 'PAYPAL',
                    }"
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="PAYPAL"
                      [(ngModel)]="selectedPaymentMethod"
                      class="hidden"
                    />
                    <span class="font-bold text-violet-300 mr-4">❖</span>
                    <span class="font-medium text-gray-100">PayPal</span>
                  </label>
                  <label
                    class="flex items-center p-4 rounded-xl cursor-pointer transition-all duration-300 bg-smoked-slate shadow-neumorphic-dark-sm hover:shadow-neumorphic-dark-sm-hover"
                    [ngClass]="{
                      '!shadow-neumorphic-dark-inset-sm':
                        selectedPaymentMethod === 'CASH_ON_DELIVERY',
                    }"
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="CASH_ON_DELIVERY"
                      [(ngModel)]="selectedPaymentMethod"
                      class="hidden"
                    />
                    <span class="font-bold text-violet-300 mr-4">❖</span>
                    <span class="font-medium text-gray-100"
                      >Pago Contra Entrega</span
                    >
                  </label>
                </div>
              </div>
              <!-- Desglose de Precios -->
              <div class="space-y-3 border-t border-white/10 pt-4">
                <div class="flex justify-between items-center text-gray-400">
                  <span>Subtotal:</span>
                  <span class="font-medium text-gray-200">{{
                    totalOrder | currency
                  }}</span>
                </div>
                <div class="flex justify-between items-center text-gray-400">
                  <span>Envío:</span>
                  <span class="font-medium text-gray-200">Gratis</span>
                </div>
                <div
                  class="flex justify-between items-center text-gray-100 font-bold text-2xl border-t border-white/20 pt-3 mt-2"
                >
                  <span>Total:</span>
                  <span class="text-violet-300">{{
                    totalOrder | currency
                  }}</span>
                </div>
              </div>
              <!-- Botón de Acción -->
              <div class="mt-8">
                <button
                  (click)="proceedToPay()"
                  [disabled]="!canProceedToPayment || isProcessing"
                  appGalaxyButton
                  appGalaxyRipple
                  class="w-full galaxy-variant-primary galaxy-size-lg"
                >
                  @if (isProcessing) {
                    <span>Procesando...</span>
                  }
                  @if (!isProcessing) {
                    @if (selectedPaymentMethod === "PAYPAL") {
                      <span>Ir a PayPal</span>
                    }
                    @if (selectedPaymentMethod === "CASH_ON_DELIVERY") {
                      <span>Confirmar Pedido</span>
                    }
                  }
                </button>
                @if (!canProceedToPayment && enrichedCartItems.length > 0) {
                  <p class="text-red-400 text-xs text-center mt-3">
                    Uno o más productos no están disponibles. Por favor,
                    elimínalos para continuar.
                  </p>
                }
              </div>
            </div>
          </app-galaxy-card>
        </div>
      </div>
    }
  </div>
</div>
